import { graphql } from 'app/generated/gql'
import { withZod } from '@remix-validated-form/with-zod'
import { ArrowRight, WarningHexagon } from 'iconoir-react'
import { isEmpty } from 'lodash'
import { ReactElement, useState } from 'react'
import { z } from 'zod'
import { useMutation } from 'urql'
import Button from '../Button'
import { useConfirmation } from '../ConfirmationContext'
import FormCombo from '../form/FormCombo'
import FormInput from '../form/FormInput'
import { SmartForm } from '../form/SmartForm'
import { closeModal } from 'app/lib/modal'
import { notifyError, notifySuccess } from 'app/services/notify.client'
import { formFieldOptions } from 'lib/formFieldDefaults'
import { logError } from 'lib/logging'
import { OptionType } from 'types/OptionType'

const formFieldMappingValidator = withZod(
  z.object({
    rawKey: z.string().min(1, { message: `Raw Key is required` }),
    formField: z.string().min(1, { message: `Form Field is required` }),
  }),
)

type FormFieldMappingRow = { id?: string; rawKey: string; formField: string }

function toOption(value: string, label?: string): OptionType {
  return { value, id: value, name: label || value }
}

const CREATE_FORM_FIELD_MAPPING = graphql(`
  mutation CreateFormFieldMapping($input: CreateFormFieldMappingInput!) {
    createFormFieldMapping(input: $input) {
      id
      rawKey
      formField
    }
  }
`)

const UPDATE_FORM_FIELD_MAPPING = graphql(`
  mutation UpdateFormFieldMapping($id: ID!, $input: UpdateFormFieldMappingInput!) {
    updateFormFieldMapping(id: $id, input: $input) {
      id
      rawKey
      formField
    }
  }
`)

const DELETE_FORM_FIELD_MAPPING = graphql(`
  mutation DeleteFormFieldMapping($id: ID!) {
    deleteFormFieldMapping(id: $id)
  }
`)

export function FormFieldMappingForm({
  mapping,
  isDefault,
  successMessage,
  onSuccess,
}: {
  mapping?: FormFieldMappingRow
  isDefault: boolean
  successMessage?: string
  onSuccess?: () => void
}): ReactElement {
  const [confirm] = useConfirmation()
  const [createResult, executeCreate] = useMutation(CREATE_FORM_FIELD_MAPPING)
  const [updateResult, executeUpdate] = useMutation(UPDATE_FORM_FIELD_MAPPING)
  const [deleteResult, executeDelete] = useMutation(DELETE_FORM_FIELD_MAPPING)
  const isLoading = createResult.fetching || updateResult.fetching || deleteResult.fetching
  const [isDuplicate, setIsDuplicate] = useState(false)

  const isEditMode = !isEmpty(mapping) && mapping?.id != null

  const [selectedFormField, setSelectedFormField] = useState<OptionType | undefined>(
    mapping?.formField
      ? toOption(mapping.formField, formFieldOptions.find((o) => o.value === mapping.formField)?.label)
      : undefined,
  )

  const handleSubmit = async (data?: Record<string, unknown>): Promise<void> => {
    if (!data) return
    setIsDuplicate(false)

    const result = await formFieldMappingValidator.validate(data)
    if (result.error) {
      logError(`error saving form field mapping`, result.error)
      notifyError(`Error Saving`)
      return
    }

    const input = { rawKey: result.data.rawKey, formField: result.data.formField }

    if (isEditMode) {
      const { error } = await executeUpdate({ id: mapping!.id!, input })
      if (error) {
        setIsDuplicate(true)
        return
      }
    } else {
      const { error } = await executeCreate({ input })
      if (error) {
        setIsDuplicate(true)
        return
      }
    }

    notifySuccess(successMessage || (isEditMode ? `Change Saved.` : `Form Field Mapping Created`))
    onSuccess?.()
    closeModal()
  }

  const handleDelete = async (id: string) => {
    if (isDefault) return
    const { error } = await executeDelete({ id }, { additionalTypenames: ['FormFieldMapping'] })
    if (error) {
      notifyError(`Error Deleting`)
      return
    }
    notifySuccess(`Deleted Successfully`)
    closeModal()
  }

  const showDeleteButton = isEditMode && !isDefault

  return (
    <SmartForm
      id="form-field-mapping-form"
      validator={formFieldMappingValidator}
      className="mb-0 mt-0 flex w-full flex-col gap-y-0"
      defaultValues={mapping}
      onSubmit={handleSubmit}
      hiddenInputs={[{ name: `id`, value: mapping?.id }]}
      hideButtons
      preventSubmit
    >
      <div className="col-span-6 flex flex-col gap-y-4 px-6 py-4">
        <div className="flex items-start gap-x-4">
          <FormInput
            labelClassName="text-strong text-body-sm font-medium"
            className="h-10 !w-64 !px-2 !py-1"
            name="rawKey"
            type="text"
            label="Raw Key"
            cols={6}
          />
          <ArrowRight className="size-5 mt-9" strokeWidth={1.5} />
          <FormCombo
            label="Form Field"
            labelClassName="text-strong text-body-sm font-medium"
            inputClassName="!h-10 !px-2 !py-1 text-body-sm font-medium !w-64"
            name="formField"
            placeholder="Select Form Field"
            value={selectedFormField}
            options={formFieldOptions.map((opt) => toOption(opt.value, opt.label))}
            onChange={setSelectedFormField}
          />
        </div>
      </div>
      <div className="border-t-trn col-span-6 flex w-full items-center justify-between border px-6 py-3">
        <Button
          mode="danger"
          size="sm"
          label="Delete"
          className={`mr-auto ${showDeleteButton ? `` : `invisible`}`}
          onClick={async () => {
            await confirm({
              title: `Delete form field mapping`,
              description: `Are you sure you want to delete the form field mapping`,
              variant: `danger`,
            }).then(() => handleDelete(mapping?.id!))
          }}
          loading={isLoading}
        />
        <div
          className={`${
            isDuplicate ? `inline-flex` : `invisible`
          } w-full items-center justify-center gap-x-2 text-center text-red-700`}
        >
          <WarningHexagon className="size-4" />
          <span className="text-body-xs">This Mapping Already Exists</span>
        </div>
        <Button
          mode="primary"
          size="sm"
          label={isEditMode ? `Update` : `Create`}
          type="submit"
          className="text-center"
          loading={isLoading}
        />
      </div>
    </SmartForm>
  )
}
